import React, { useState, useEffect, useCallback } from 'react';
import { message, Tooltip } from '../common';
import TagInput from './TagInput';
import { copyToClipboard } from '../../utils/clipboard';

// Mock API
const toolApi = {
    getList: (params) => {
        const query = new URLSearchParams(params).toString();
        const token = localStorage.getItem('token');
        return fetch(`/api/tools/quick-copy?${query}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => res.json());
    },
    create: (data) => {
        const token = localStorage.getItem('token');
        return fetch('/api/tools/quick-copy', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(data)
        }).then(res => res.json());
    },
    update: (id, data) => {
        const token = localStorage.getItem('token');
        return fetch(`/api/tools/quick-copy/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(data)
        }).then(res => res.json());
    },
    delete: (id) => {
        const token = localStorage.getItem('token');
        return fetch(`/api/tools/quick-copy/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => res.json());
    },
    import: (data) => {
        const token = localStorage.getItem('token');
        return fetch('/api/tools/quick-copy/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(data)
        }).then(res => res.json());
    },
    getTags: (scene) => {
        const token = localStorage.getItem('token');
        return fetch(`/api/tools/tags?scene=${scene}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => res.json());
    }
};

const QuickCopyPlugin = () => {
    // 1. All useState hooks at top
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState([]);
    const [filters, setFilters] = useState({ keyword: '', tag_id: '' });

    const [importVisible, setImportVisible] = useState(false);
    const [importData, setImportData] = useState('');
    const [importName, setImportName] = useState('');
    const [importTags, setImportTags] = useState([]);
    const [importPriority, setImportPriority] = useState(0);

    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');

    // New state for content editing
    const [editingContentId, setEditingContentId] = useState(null);
    const [editContent, setEditContent] = useState('');

    // 2. Define functions
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await toolApi.getList(filters);
            if (res.code === 200) setItems(res.data || []);
        } catch (e) {
            console.error('Fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchTags = async () => {
        try {
            const res = await toolApi.getTags('quick_copy');
            if (res.code === 200) setTags(res.data || []);
        } catch (e) {
            console.error(e);
        }
    };

    // 3. useEffect hooks
    useEffect(() => {
        fetchTags();
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    // Handlers
    const handleUpdateName = async (id) => {
        if (!editName.trim()) return setEditingId(null);
        try {
            await toolApi.update(id, { name: editName });
            message.success('更新成功');
            setEditingId(null);
            fetchData();
        } catch (e) {
            message.error('更新失败');
        }
    };

    const handleUpdateTags = async (id, newTags) => {
        try {
            await toolApi.update(id, { tags: newTags });
            message.success('标签更新成功');
            fetchData();
            fetchTags(); // Update global tags
        } catch (e) {
            message.error('标签更新失败');
        }
    }

    const handleUpdatePriority = async (id, newPriority) => {
        try {
            await toolApi.update(id, { priority: parseInt(newPriority) || 0 });
            message.success('优先级更新成功');
            fetchData();
        } catch (e) {
            message.error('优先级更新失败');
        }
    };

    // New handler for content update
    const handleUpdateContent = async (id) => {
        if (!editContent.trim()) return;
        try {
            let content = editContent;
            try {
                // Try to parse as JSON to save valid JSON object
                const parsed = JSON.parse(editContent);
                if (typeof parsed === 'object' && parsed !== null) {
                    content = parsed;
                }
            } catch (e) {
                // Keep as string if not valid JSON
            }

            await toolApi.update(id, { content });
            message.success('内容更新成功');
            setEditingContentId(null);
            fetchData();
        } catch (e) {
            message.error('内容更新失败');
        }
    };

    const startEditContent = (item) => {
        setEditingContentId(item.id);
        const contentStr = typeof item.content === 'object' ? JSON.stringify(item.content, null, 2) : String(item.content);
        setEditContent(contentStr);
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditName(item.name || '');
    };

    const handleImport = async () => {
        if (!importData.trim()) return;
        try {
            let type = 'text';
            try {
                JSON.parse(importData);
                type = 'json';
            } catch (e) { }

            const res = await toolApi.import({
                text: importData,
                type,
                name: importName,
                tags: importTags,
                priority: parseInt(importPriority) || 0
            });
            if (res.code === 200) {
                message.success('导入成功');
                setImportVisible(false);
                setImportData('');
                setImportName('');
                setImportTags([]);
                setImportPriority(0);
                fetchData();
                fetchTags();
            } else {
                message.error(res.message);
            }
        } catch (e) {
            message.error('导入失败');
        }
    };

    const handleDelete = async (id) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('确定删除?')) return;
        try {
            const res = await toolApi.delete(id);
            if (res.code === 200) {
                message.success('删除成功');
                fetchData();
            } else {
                message.error(res.message || '删除失败');
            }
        } catch (e) {
            message.error('删除失败');
        }
    };

    const handleCopy = (text, label) => {
        copyToClipboard(text).then(success => {
            if (success) {
                message.success(`已复制: ${label || text}`);
            } else {
                message.error('复制失败');
            }
        });
    };

    const renderTree = (data, pKey = '') => {
        if (typeof data !== 'object' || data === null) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '2px 0' }}>
                    <span style={{ color: '#666', wordBreak: 'break-all' }}>{String(data)}</span>
                    <Tooltip text="复制">
                        <span
                            style={{ cursor: 'pointer', opacity: 0.6 }}
                            onClick={() => handleCopy(String(data), String(data))}
                        >📋</span>
                    </Tooltip>
                </div>
            );
        }

        return Object.keys(data).map(key => {
            const value = data[key];
            const isLeaf = typeof value !== 'object' || value === null;

            return (
                <div key={pKey + key} style={{ marginLeft: '15px', borderLeft: '1px solid #eee', paddingLeft: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <strong>{key}{isLeaf ? ':' : ''}</strong>
                        <Tooltip text={`复制 Key: ${key}`}>
                            <span
                                style={{ cursor: 'pointer', fontSize: '12px', opacity: 0.5 }}
                                onClick={() => handleCopy(key, key)}
                            >📑</span>
                        </Tooltip>
                        {isLeaf && (
                            <>
                                <span style={{ wordBreak: 'break-all' }}>{String(value)}</span>
                                <Tooltip text={`复制 Value: ${value}`}>
                                    <span
                                        style={{ cursor: 'pointer', color: '#1890ff' }}
                                        onClick={() => handleCopy(String(value), value)}
                                    >📋</span>
                                </Tooltip>
                            </>
                        )}
                    </div>
                    {!isLeaf && renderTree(value, pKey + key + '.')}
                </div>
            );
        });
    };

    return (
        <div className="quick-copy-plugin" style={{ padding: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Search Bar */}
            <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <input
                    placeholder="搜索名称或内容..."
                    value={filters.keyword}
                    onChange={e => setFilters({ ...filters, keyword: e.target.value })}
                    style={{ flex: 1, padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
                <select
                    value={filters.tag_id}
                    onChange={e => setFilters({ ...filters, tag_id: e.target.value })}
                    style={{ width: '80px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">全部标签</option>
                    {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <button className="btn btn-primary btn-small" onClick={() => setImportVisible(true)}>导入</button>
            </div>

            {/* Content List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {items && items.map(item => (
                    <div key={item.id} className="card" style={{ marginBottom: '10px', padding: '10px', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                                {editingId === item.id ? (
                                    <input
                                        autoFocus
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onBlur={() => handleUpdateName(item.id)}
                                        onKeyDown={e => e.key === 'Enter' && handleUpdateName(item.id)}
                                        style={{ border: '1px solid #1890ff', padding: '2px 5px', borderRadius: '4px', width: '60%' }}
                                    />
                                ) : (
                                    <h4
                                        style={{ margin: 0, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                        onClick={() => startEdit(item)}
                                        title="点击修改名称"
                                    >
                                        {item.name || '未命名'}
                                        <span style={{ fontSize: '10px', color: '#999' }}>✎</span>
                                    </h4>
                                )}
                                {/* Priority Input */}
                                <input
                                    type="number"
                                    defaultValue={item.priority}
                                    onBlur={(e) => handleUpdatePriority(item.id, e.target.value)}
                                    style={{ width: '40px', padding: '2px', fontSize: '12px', border: '1px solid #eee', borderRadius: '4px', textAlign: 'center' }}
                                    title="优先级 (越大越靠前)"
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Tooltip text="复制全部">
                                    <button
                                        className="btn-link"
                                        onClick={() => handleCopy(
                                            typeof item.content === 'object' ? JSON.stringify(item.content, null, 2) : String(item.content),
                                            '全部内容'
                                        )}
                                        style={{ color: '#1890ff', fontSize: '14px', lineHeight: 1 }}
                                    >📋</button>
                                </Tooltip>
                                <button className="btn-link" onClick={() => handleDelete(item.id)} style={{ color: '#ff4d4f', fontSize: '16px', lineHeight: 1 }}>×</button>
                            </div>
                        </div>

                        {/* Content Area with Edit Switch */}
                        <div style={{ position: 'relative' }}>
                            {editingContentId === item.id ? (
                                <div style={{ marginTop: '5px' }}>
                                    <textarea
                                        value={editContent}
                                        onChange={e => setEditContent(e.target.value)}
                                        style={{ width: '100%', minHeight: '150px', padding: '5px', border: '1px solid #1890ff', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}
                                    />
                                    <div style={{ marginTop: '5px', display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                                        <button className="btn btn-small btn-default" onClick={() => setEditingContentId(null)}>取消</button>
                                        <button className="btn btn-small btn-primary" onClick={() => handleUpdateContent(item.id)}>保存</button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ position: 'relative', marginTop: '5px' }}>
                                    {renderTree(item.content)}
                                    <button
                                        className="btn-link"
                                        style={{ position: 'absolute', right: '0', top: '0', fontSize: '12px', color: '#1890ff', opacity: 0.6 }}
                                        onClick={() => startEditContent(item)}
                                    >
                                        编辑内容
                                    </button>
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: '10px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            <TagInput
                                tags={item.tags ? item.tags.map(t => t.name) : []}
                                onChange={(newTags) => handleUpdateTags(item.id, newTags)}
                                suggestions={tags}
                            />
                        </div>
                    </div>
                ))}
                {items && items.length === 0 && !loading && <div className="text-center" style={{ color: '#999' }}>暂无数据</div>}
            </div>

            {/* Import Modal */}
            {importVisible && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal" style={{ width: '400px' }}>
                        <div className="modal-header">
                            <h3>快捷导入</h3>
                            <button className="modal-close" onClick={() => setImportVisible(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '3px', color: '#666' }}>名称 (可选)</label>
                                    <input
                                        value={importName}
                                        onChange={e => setImportName(e.target.value)}
                                        placeholder="默认为内容前20个字符"
                                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                                    />
                                </div>
                                <div style={{ width: '80px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '3px', color: '#666' }}>优先级</label>
                                    <input
                                        type="number"
                                        value={importPriority}
                                        onChange={e => setImportPriority(e.target.value)}
                                        style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                                    />
                                </div>
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '3px', color: '#666' }}>标签</label>
                                <TagInput
                                    tags={importTags}
                                    onChange={setImportTags}
                                    suggestions={tags}
                                />
                            </div>
                            <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                                内容 (Key: Value 或 JSON):
                            </p>
                            <textarea
                                rows={8}
                                style={{ width: '100%', padding: '5px' }}
                                value={importData}
                                onChange={e => setImportData(e.target.value)}
                                placeholder="请输入内容..."
                            />
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={handleImport}>确认导入</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickCopyPlugin;
